import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Skeletons, ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, HardHat, Building2, Phone, Mail, ArrowLeft, ArrowRight, Briefcase, Loader2, MapPin, Trash2, Archive, Paperclip, Pencil, DollarSign, ListChecks, Search, Users, X, Send } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STAGES = [
  { key: "rough_in", label: "Rough In", hint: "Inwalls and Mixers & Sinks" },
  { key: "tiling", label: "Tiles", hint: "Tiles & Mosaic" },
  { key: "tapware", label: "Plumbing Fixtures", hint: "Showers, Toilets, Bathtub" },
  { key: "flooring", label: "Flooring", hint: "Hybrid, Engineered" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

function BuilderFormDialog({ onSaved, builder = null, trigger, triggerTestId = "add-builder-btn" }) {
  const isEdit = !!builder;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", address: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) {
      setForm(isEdit
        ? { name: builder.name || "", contact_name: builder.contact_name || "", phone: builder.phone || "", email: builder.email || "", address: builder.address || "" }
        : { name: "", contact_name: "", phone: "", email: "", address: "" });
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Builder name is required"); return; }
    setSaving(true);
    try {
      const { data } = isEdit
        ? await client.put(`/builders/${builder.id}`, form)
        : await client.post("/builders", form);
      toast.success(isEdit ? "Builder updated" : "Builder added");
      setOpen(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Could not update builder" : "Could not add builder"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {trigger || <Button className="gap-2" data-testid={triggerTestId}><Plus className="h-4 w-4" />Add builder</Button>}
      </DialogTrigger>
      <DialogContent data-testid={isEdit ? "edit-builder-dialog" : "add-builder-dialog"}>
        <DialogHeader><DialogTitle className="font-display text-xl">{isEdit ? "Edit builder" : "Add a builder"}</DialogTitle><DialogDescription>{isEdit ? "Update this builder's contact details." : "Builders are tracked separately from showroom customers."}</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5"><Label>Builder / company name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Metro Constructions" data-testid="builder-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="builder-contact-input" /></div>
            <div className="grid gap-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="builder-phone-input" /></div>
          </div>
          <div className="grid gap-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="accounts@builder.com.au" data-testid="builder-email-input" /></div>
          <div className="grid gap-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="builder-address-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="builder-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isEdit ? "Save changes" : "Save builder"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AddBuilderDialog = BuilderFormDialog;

function PortalAccessCard({ builderId }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const contacts = useQuery({ queryKey: ["portal-contacts", builderId], queryFn: async () => (await client.get(`/builder-portal/admin/builders/${builderId}/contacts`)).data });
  const reqs = useQuery({ queryKey: ["portal-cr", builderId], queryFn: async () => (await client.get(`/builder-portal/admin/change-requests`, { params: { builder_id: builderId } })).data });
  const logins = useQuery({ queryKey: ["portal-logins", builderId], queryFn: async () => (await client.get(`/builder-portal/admin/builders/${builderId}/logins`)).data });
  const add = async () => {
    if (phone.trim().length < 8) { toast.error("Enter a mobile number"); return; }
    setBusy(true);
    try {
      await client.post(`/builder-portal/admin/builders/${builderId}/contacts`, { name, phone });
      toast.success("Portal access added"); setName(""); setPhone("");
      qc.invalidateQueries({ queryKey: ["portal-contacts", builderId] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const del = async (cid) => {
    try { await client.delete(`/builder-portal/admin/contacts/${cid}`); qc.invalidateQueries({ queryKey: ["portal-contacts", builderId] }); }
    catch { toast.error("Could not remove"); }
  };
  const invite = async (cid) => {
    setInvitingId(cid);
    try {
      await client.post(`/builder-portal/admin/contacts/${cid}/invite`);
      toast.success("Portal invite sent");
      qc.invalidateQueries({ queryKey: ["portal-contacts", builderId] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invite"); }
    finally { setInvitingId(null); }
  };
  const action = async (crid) => {
    try { await client.post(`/builder-portal/admin/change-requests/${crid}/actioned`); qc.invalidateQueries({ queryKey: ["portal-cr", builderId] }); }
    catch { toast.error("Could not update"); }
  };
  const list = contacts.data || [];
  const openReqs = (reqs.data || []).filter((r) => r.status === "open");
  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="portal-access-card">
      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /><h2 className="font-display text-base font-bold text-ink">Builder portal access (SMS login)</h2></div>
      <p className="mt-0.5 text-xs text-muted-foreground">Add mobile numbers that can sign in at <span className="font-mono">/portal</span> to view this builder's sites. Pricing is never shown.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Contact name (optional)" data-testid="portal-contact-name" />
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile e.g. 0412 345 678" inputMode="tel" data-testid="portal-contact-phone" />
        <Button onClick={add} disabled={busy} className="gap-1.5 sm:w-40" data-testid="portal-contact-add"><Plus className="h-4 w-4" />Add</Button>
      </div>
      {list.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="portal-contact-list">
          {list.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2 text-sm">
              <span className="min-w-0 truncate"><span className="font-semibold text-ink">{c.name || "—"}</span><span className="text-muted-foreground"> · {c.phone}</span>{c.last_login_at && <span className="text-[11px] text-muted-foreground"> · active</span>}{c.invited_at && !c.last_login_at && <span className="text-[11px] text-muted-foreground"> · invited</span>}</span>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => invite(c.id)} disabled={invitingId === c.id} data-testid={`portal-contact-invite-${c.id}`} className="flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50">
                  {invitingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{c.invited_at ? "Resend" : "Invite"}
                </button>
                <button onClick={() => del(c.id)} data-testid={`portal-contact-del-${c.id}`} className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-error"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {openReqs.length > 0 && (
        <div className="mt-4" data-testid="portal-change-requests">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Open change requests</h3>
          <div className="space-y-1.5">
            {openReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#D97706]/30 bg-[#D97706]/5 px-3 py-2 text-sm">
                <span className="min-w-0"><span className="font-semibold text-ink">{r.job_title}</span><span className="text-muted-foreground"> — {r.type}{r.stage_label ? ` (${r.stage_label})` : ""}{r.requested_date ? ` · ${r.requested_date}` : ""}{r.message ? ` · ${r.message}` : ""} — {r.created_by_name}</span></span>
                <Button size="sm" variant="outline" className="shrink-0 gap-1" onClick={() => action(r.id)} data-testid={`portal-cr-action-${r.id}`}>Mark actioned</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {(logins.data || []).length > 0 && (
        <div className="mt-4" data-testid="portal-logins">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Recent portal logins</h3>
          <div className="space-y-1">
            {logins.data.slice(0, 8).map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 text-xs text-muted-foreground" data-testid={`portal-login-${l.id}`}>
                <span className="min-w-0 truncate"><span className="font-semibold text-ink">{l.name || "—"}</span> · {l.phone}</span>
                <span className="shrink-0">{new Date(l.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddJobDialog({ builderId, onSaved, triggerTestId = "add-job-btn" }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [address, setAddress] = useState("");
  const [stages, setStages] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setStages((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const submit = async () => {
    if (!title.trim()) { toast.error("Job title is required"); return; }
    if (stages.length === 0) { toast.error("Select at least one stage this builder is using"); return; }
    setSaving(true);
    try {
      const { data } = await client.post(`/builders/${builderId}/jobs`, { title, address, stages });
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try { await client.post(`/builder-jobs/${data.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch { /* non-fatal */ }
      }
      toast.success(files.length ? `Job created · ${files.length} file(s) uploaded` : "Job created");
      setTitle(""); setAddress(""); setStages([]); setFiles([]); setOpen(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create job");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid={triggerTestId}><Plus className="h-4 w-4" />New job</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-job-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">New builder job</DialogTitle><DialogDescription>Pick which stages we're supplying for this job — not every builder uses all four.</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5"><Label>Job title / site</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 42 Smith St — Ensuite" data-testid="job-title-input" /></div>
          <div className="grid gap-1.5"><Label>Site address (optional)</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="job-address-input" /></div>
          <div className="grid gap-2">
            <Label>Applicable stages</Label>
            <div className="grid gap-2">
              {STAGES.map((s) => (
                <label key={s.key} data-testid={`stage-toggle-${s.key}`} className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3 transition-colors hover:bg-muted/40">
                  <Checkbox checked={stages.includes(s.key)} onCheckedChange={() => toggle(s.key)} className="mt-0.5" />
                  <span><span className="block text-sm font-semibold text-ink">{s.label}</span><span className="block text-xs text-muted-foreground">{s.hint}</span></span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" />Attach plans / files (optional)</Label>
            <Input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="job-files-input" />
            {files.length > 0 && <p className="text-xs text-muted-foreground">{files.length} file(s) selected — visible to sales staff & admins only.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="job-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create job</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BuilderCard({ b, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`builder-card-${b.id}`}
      className="group flex flex-col rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_8px_24px_rgba(35,52,90,0.08)]">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><HardHat className="h-5 w-5" strokeWidth={1.75} /></div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-base font-bold text-ink">{b.name}</h3>
          {b.contact_name && <p className="truncate text-xs text-muted-foreground">{b.contact_name}</p>}
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <div className="mt-4 flex items-center gap-4 border-t border-slate-100 pt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{b.job_count} {b.job_count === 1 ? "job" : "jobs"}</span>
        {b.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{b.phone}</span>}
      </div>
    </button>
  );
}

function groupBuildersAlpha(builders) {
  const sorted = [...builders].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
  const groups = new Map();
  for (const b of sorted) {
    const first = (b.name || "").trim().charAt(0).toUpperCase();
    const letter = /[A-Z]/.test(first) ? first : "#";
    if (!groups.has(letter)) groups.set(letter, []);
    groups.get(letter).push(b);
  }
  const entries = [...groups.entries()];
  entries.sort((a, b) => (a[0] === "#" ? 1 : b[0] === "#" ? -1 : 0)); // "#" bucket always last
  return entries;
}

function BuilderList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["builders"], queryFn: async () => (await client.get("/builders")).data });
  const builders = data || [];
  const term = q.trim().toLowerCase();
  const filtered = term
    ? builders.filter((b) =>
        (b.name || "").toLowerCase().includes(term) || (b.contact_name || "").toLowerCase().includes(term))
    : builders;
  const groups = groupBuildersAlpha(filtered);
  const present = new Set(groups.map(([l]) => l));
  const showRail = groups.length >= 6;
  const RAIL = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""), "#"];
  const jumpTo = (letter) => {
    document.getElementById(`bgroup-${letter}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Builders Hub" subtitle="Track builder jobs and per-stage selections separately from showroom leads" testid="builders-header">
        <AddBuilderDialog onSaved={() => qc.invalidateQueries({ queryKey: ["builders"] })} />
      </PageHeader>

      {!isLoading && !isError && builders.length > 0 && (
        <div className="relative mb-6 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search builders by name or contact…"
            className="pl-9"
            data-testid="builders-search-input"
          />
        </div>
      )}

      {isError ? (
        <ErrorState onRetry={refetch} message="We couldn't load your builders. Please try again." />
      ) : isLoading ? (
        <Skeletons />
      ) : builders.length === 0 ? (
        <EmptyState icon={HardHat} title="No builders yet" hint="Add a builder to start tracking their jobs and stage-by-stage selections." action={<AddBuilderDialog triggerTestId="empty-add-builder-btn" onSaved={() => qc.invalidateQueries({ queryKey: ["builders"] })} />} />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 p-10 text-center" data-testid="builders-no-results">
          <p className="text-sm text-muted-foreground">No builders match "<span className="font-semibold text-ink">{q}</span>".</p>
        </div>
      ) : (
        <div className={cn("space-y-6", showRail && "pr-7 sm:pr-0")} data-testid="builders-grouped">
          {groups.map(([letter, list]) => (
            <section key={letter} id={`bgroup-${letter}`} className="scroll-mt-20" data-testid={`builder-group-${letter}`}>
              <div className="sticky top-0 z-10 -mx-1 mb-3 flex items-center gap-3 bg-background/85 px-1 py-2 backdrop-blur">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 font-display text-sm font-extrabold text-primary" data-testid={`builder-group-letter-${letter}`}>{letter}</span>
                <div className="h-px flex-1 bg-slate-100" />
                <span className="text-xs font-semibold text-muted-foreground">{list.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {list.map((b) => (
                  <BuilderCard key={b.id} b={b} onOpen={() => navigate(`/builders/${b.id}`)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {showRail && (
        <nav
          className="fixed right-1 top-1/2 z-20 flex max-h-[88vh] -translate-y-1/2 flex-col items-center gap-0.5 rounded-full border border-slate-100 bg-white/90 px-1 py-2 shadow-[0_4px_16px_rgba(35,52,90,0.10)] backdrop-blur"
          aria-label="Jump to letter"
          data-testid="builders-az-rail"
        >
          {RAIL.map((letter) => {
            const has = present.has(letter);
            return (
              <button
                key={letter}
                type="button"
                disabled={!has}
                onClick={() => jumpTo(letter)}
                data-testid={`az-jump-${letter}`}
                className={cn(
                  "h-4 w-5 rounded text-[10px] font-bold leading-none transition-colors sm:h-[18px]",
                  has ? "text-primary hover:bg-primary hover:text-white active:bg-primary active:text-white" : "cursor-default text-slate-300"
                )}
              >
                {letter}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

function JobCard({ j, archived, onOpen, onDeleted }) {
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const done = (j.stages || []).filter((s) => j.stage_data?.[s]?.status === "done").length;
  const del = async () => {
    setDeleting(true);
    try {
      await client.delete(`/builder-jobs/${j.id}`);
      toast.success("Job deleted");
      setDelOpen(false);
      onDeleted?.();
    } catch { toast.error("Could not delete job"); }
    finally { setDeleting(false); }
  };
  return (
    <div onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }} data-testid={`job-card-${j.id}`}
      className={cn("group flex cursor-pointer flex-col rounded-2xl border bg-white p-5 text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_8px_24px_rgba(35,52,90,0.08)]", archived ? "border-slate-100 opacity-80" : "border-slate-100")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-[15px] font-bold text-ink">{j.title}</h3>
          {j.address && <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{j.address}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold", archived ? "bg-[#D1FAE5] text-[#065F46]" : "bg-accent text-accent-foreground")}>{done}/{(j.stages || []).length} done</span>
          <button onClick={(e) => { e.stopPropagation(); setDelOpen(true); }} data-testid={`delete-job-${j.id}`} title="Delete job" className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-error"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
        {(j.stages || []).map((s) => {
          const isDone = j.stage_data?.[s]?.status === "done";
          return <span key={s} className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${isDone ? "border-[#A7F3D0] bg-[#D1FAE5] text-[#065F46]" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{STAGE_LABEL[s]}</span>;
        })}
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid={`delete-job-dialog-${j.id}`} onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle className="font-display text-xl">Delete this job?</DialogTitle><DialogDescription>"{j.title}" and all its stage selections will be permanently removed. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button onClick={del} disabled={deleting} data-testid={`confirm-delete-job-${j.id}`} className="gap-2 bg-error text-white hover:bg-error/90">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Delete job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResponsibleSalesCard({ builder, isAdmin, onSaved }) {
  const assignedIds = builder.assigned_rep_ids || [];
  const assignedNames = builder.assigned_rep_names || [];
  const [saving, setSaving] = useState(false);
  const usersQ = useQuery({ queryKey: ["team-users"], enabled: !!isAdmin, queryFn: async () => (await client.get("/users")).data });
  const salesUsers = (usersQ.data || []).filter((u) => ["admin", "rep"].includes(u.role));
  const save = async (ids) => {
    setSaving(true);
    try { await client.put(`/builders/${builder.id}/responsible-reps`, { rep_ids: ids }); toast.success("Responsible sales updated"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setSaving(false); }
  };
  const addRep = (uid) => { if (uid && !assignedIds.includes(uid)) save([...assignedIds, uid]); };
  const removeRep = (uid) => save(assignedIds.filter((x) => x !== uid));
  const available = salesUsers.filter((u) => !assignedIds.includes(u.user_id));
  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="responsible-sales-card">
      <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><h2 className="font-display text-base font-bold text-ink">Responsible sales</h2></div>
      <p className="mt-0.5 text-xs text-muted-foreground">The sales rep(s) accountable for this builder. The whole sales team can see and action change requests; this records who owns the relationship.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="responsible-sales-list">
        {assignedIds.length === 0 && <span className="text-sm text-muted-foreground">No one assigned yet.</span>}
        {assignedIds.map((uid, i) => (
          <span key={uid} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary" data-testid={`responsible-rep-${uid}`}>
            {assignedNames[i] || "—"}
            {isAdmin && <button onClick={() => removeRep(uid)} disabled={saving} data-testid={`responsible-rep-remove-${uid}`} className="grid place-items-center rounded-full hover:text-error"><X className="h-3.5 w-3.5" /></button>}
          </span>
        ))}
      </div>
      {isAdmin && available.length > 0 && (
        <div className="mt-3">
          <select data-testid="responsible-rep-add" disabled={saving} value="" onChange={(e) => addRep(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
            <option value="">+ Add responsible rep…</option>
            {available.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function BuilderDetail({ builderId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data: b, isLoading, isError, refetch } = useQuery({ queryKey: ["builder", builderId], queryFn: async () => (await client.get(`/builders/${builderId}`)).data });

  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (isError) return <ErrorState onRetry={refetch} message="We couldn't load this builder." />;
  if (!b) return <div className="py-20 text-center text-muted-foreground">Builder not found.</div>;

  const delBuilder = async () => {
    setDeleting(true);
    try {
      await client.delete(`/builders/${builderId}`);
      toast.success("Builder deleted");
      navigate("/builders");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete builder"); }
    finally { setDeleting(false); }
  };

  const refresh = () => { qc.invalidateQueries({ queryKey: ["builder", builderId] }); qc.invalidateQueries({ queryKey: ["builders"] }); };
  const jobs = b.jobs || [];
  const activeJobs = jobs.filter((j) => !j.archived);
  const archivedJobs = jobs.filter((j) => j.archived);

  const fmtAUD = (n) => n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
  const lifetime = jobs.reduce((sum, j) => sum + (j.stages || []).reduce((a, s) => a + (j.stage_data?.[s]?.total || 0), 0), 0);
  const outstandingStages = activeJobs.reduce((a, j) => a + (j.stages || []).filter((s) => j.stage_data?.[s]?.status !== "done").length, 0);
  const stats = [
    { icon: DollarSign, label: "Lifetime value", value: fmtAUD(lifetime), testid: "builder-stat-lifetime" },
    { icon: Briefcase, label: "Open jobs", value: activeJobs.length, testid: "builder-stat-open-jobs" },
    { icon: ListChecks, label: "Outstanding stages", value: outstandingStages, testid: "builder-stat-outstanding" },
  ];

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/builders")} data-testid="back-to-builders" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> All builders
      </button>

      <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary"><HardHat className="h-6 w-6" /></div>
            <div>
              <h1 className="font-display text-2xl font-extrabold text-ink" data-testid="builder-name">{b.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {b.contact_name && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{b.contact_name}</span>}
                {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone className="h-3.5 w-3.5" />{b.phone}</a>}
                {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-primary"><Mail className="h-3.5 w-3.5" />{b.email}</a>}
                {b.address && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{b.address}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BuilderFormDialog
              builder={b}
              onSaved={refresh}
              trigger={<Button variant="outline" className="gap-2" data-testid="edit-builder-btn"><Pencil className="h-4 w-4" />Edit builder</Button>}
            />
            <AddJobDialog builderId={builderId} onSaved={refresh} />
            {isAdmin && (
              <Button variant="outline" onClick={() => setDelOpen(true)} data-testid="delete-builder-btn" className="gap-2 text-error hover:text-error"><Trash2 className="h-4 w-4" />Delete builder</Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="delete-builder-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Delete this builder?</DialogTitle><DialogDescription>"{b.name}", all {jobs.length} of its {jobs.length === 1 ? "job" : "jobs"} and every stage selection will be permanently removed. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button onClick={delBuilder} disabled={deleting} data-testid="confirm-delete-builder" className="gap-2 bg-error text-white hover:bg-error/90">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Delete builder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mb-6 grid grid-cols-3 gap-3" data-testid="builder-account-stats">
        {stats.map((s) => (
          <div key={s.testid} data-testid={s.testid} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><s.icon className="h-3.5 w-3.5 text-primary" />{s.label}</div>
            <div className="mt-1.5 font-display text-xl font-extrabold text-ink sm:text-2xl">{s.value}</div>
          </div>
        ))}
      </div>

      <ResponsibleSalesCard builder={b} isAdmin={isAdmin} onSaved={refresh} />

      <PortalAccessCard builderId={builderId} />

      <h2 className="mb-3 font-display text-base font-bold text-ink">Jobs</h2>
      {jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="No jobs yet" hint="Create a job and pick which stages you're supplying." action={<AddJobDialog builderId={builderId} triggerTestId="empty-add-job-btn" onSaved={refresh} />} />
      ) : (
        <>
          {activeJobs.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="active-jobs">
              {activeJobs.map((j) => <JobCard key={j.id} j={j} onOpen={() => navigate(`/builder-jobs/${j.id}`)} onDeleted={refresh} />)}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-border bg-white/60 p-6 text-center text-sm text-muted-foreground">No active jobs — everything's done. See the Archive below.</p>
          )}
          {archivedJobs.length > 0 && (
            <div className="mt-8" data-testid="archived-jobs">
              <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold text-muted-foreground">
                <Archive className="h-4 w-4" /> Archive
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{archivedJobs.length}</span>
              </h2>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {archivedJobs.map((j) => <JobCard key={j.id} j={j} archived onOpen={() => navigate(`/builder-jobs/${j.id}`)} onDeleted={refresh} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function BuildersHub() {
  const { builderId } = useParams();
  return builderId ? <BuilderDetail builderId={builderId} /> : <BuilderList />;
}
